import * as React from 'react';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import LocationOnRoundedIcon from '@mui/icons-material/LocationOnRounded';

import PageTemplate from '../../components/PageTemplate';
import { useApp } from '../../../context';
import { getLocations } from '../../../utils/platformConfig';
import { LOCATIONS as CODE_LOCATIONS } from '../../../data/locations';

/**
 * Useful info — what is on Vhakacha and where it is.
 *
 * WHAT THIS PAGE DOES NOT DO. The obvious reading of "useful info" is travel
 * guidance: visas, currency, when to visit. Every one of those is a checkable
 * claim that changes without telling us, and getting one wrong on a booking
 * site is worse than not answering it. Nothing here is written by us — the
 * categories come from the platform taxonomy and the destinations from
 * `app_config`, so the page cannot go stale independently of the app.
 *
 * If real guidance is wanted, it belongs in a document an operator or an admin
 * maintains, with a date on it.
 */
export default function UsefulInfo() {
  const { serviceFilters } = useApp();
  const [locations, setLocations] = React.useState(CODE_LOCATIONS);

  React.useEffect(() => {
    let live = true;
    getLocations().then((next) => {
      if (live && next?.length) setLocations(next);
    });
    return () => { live = false; };
  }, []);

  const categories = Array.isArray(serviceFilters?.categories) ? serviceFilters.categories : [];

  // Grouped by region, because "where can I go" is a map question before it is
  // an alphabetical one.
  const byRegion = React.useMemo(() => {
    const map = {};
    locations.forEach((location) => {
      (map[location.region || 'Elsewhere'] ||= []).push(location);
    });
    return map;
  }, [locations]);

  return (
    <PageTemplate>
      <Toolbar />
      <Box sx={{ px: { xs: 2, sm: 3 }, pt: 2, pb: 6, maxWidth: 900, mx: 'auto' }}>
        <Button component={Link} to="/" variant="text" startIcon={<ChevronLeftIcon />} sx={{ px: 0, mb: 1 }}>
          Back
        </Button>

        <Typography variant="h4" component="h1" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
          Useful info
        </Typography>
        <Typography color="text.secondary" sx={{ mt: 0.5, maxWidth: '62ch' }}>
          What you can book on Vhakacha, and where in Zimbabwe you can book it.
        </Typography>

        {categories.length > 0 && (
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" component="h2" sx={{ fontWeight: 800, mb: 1.5 }}>
              What you can book
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {categories.map((category) => (
                <Chip
                  key={category}
                  label={category}
                  component={Link}
                  to={`/search?type=${encodeURIComponent(category)}`}
                  clickable
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>
        )}

        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" component="h2" sx={{ fontWeight: 800, mb: 1.5 }}>
            Where you can go
          </Typography>
          <Box sx={{ display: 'grid', gap: 2.5, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' } }}>
            {Object.entries(byRegion).map(([region, places]) => (
              <Box key={region}>
                <Typography
                  variant="overline"
                  sx={{ color: 'text.secondary', fontWeight: 800, letterSpacing: '0.12em' }}
                >
                  {region}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.5 }}>
                  {places.map((place) => (
                    <Chip
                      key={place.value}
                      size="small"
                      icon={<LocationOnRoundedIcon />}
                      label={place.label || place.value}
                      component={Link}
                      to={`/search?location=${encodeURIComponent(place.value)}`}
                      clickable
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        <Box sx={{ mt: 5, pt: 3, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="h6" component="h2" sx={{ fontWeight: 800, mb: 1 }}>
            About Vhakacha
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '62ch' }}>
            Vhakacha lists stays, tours and experiences across Zimbabwe. Every operator is checked before their
            listings can be published, and reviews can only be left by travellers who completed a booking.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Zimbabwe, Africa · © {new Date().getFullYear()} Vhakacha
          </Typography>
        </Box>
      </Box>
    </PageTemplate>
  );
}
